import { useState } from 'react';
import { Link } from 'react-router-dom';

import { pacificDayString, shiftPacificDay } from '../../shared/dates';
import { useAppState } from '../lib/appState';
import { usePunFeed, type FeedRange, type FeedScope } from '../lib/usePunFeed';
import PunCard from './PunCard';

/** How far back "this week" reaches, counting today. */
const WEEK_DAYS = 7;

type Period = 'today' | 'yesterday' | 'week';

const PERIOD_LABEL: Record<Period, string> = {
  today: 'Today',
  yesterday: 'Yesterday',
  week: 'This week',
};

/**
 * The feed. Today's puns once you have written one; earlier days always.
 *
 * The gate is on *today* specifically, and that is the whole of it. Reading
 * everyone else's answer before writing your own turns the prompt from a puzzle
 * into a survey — but that argument expires the moment a day is settled, and
 * locking the archive along with it only hid the thing that makes a new player
 * want to play.
 */
function Feed() {
  const { userId, submittedPun, checkingSubmission, submissionCheckFailed, recheckSubmission } =
    useAppState();
  const [scope, setScope] = useState<Extract<FeedScope, 'everyone' | 'friends'>>('everyone');
  const [period, setPeriod] = useState<Period>('today');

  const today = pacificDayString();
  const yesterday = shiftPacificDay(today, -1);

  /*
   * Only a *known* "you have not posted" locks anything. While the check is in
   * flight, and if it fails outright, we are about to render something else
   * entirely — and treating "we do not know" as "you have not posted" is the
   * bug this screen kept hitting.
   */
  const locked = !checkingSubmission && !submissionCheckFailed && submittedPun === null;

  /*
   * Derived rather than stored, so the tab cannot be left pointing at a locked
   * day. Initialising the state from `locked` instead would read it before the
   * submission check has answered, and strand a player who *had* posted on
   * Yesterday.
   */
  const shown: Period = locked && period === 'today' ? 'yesterday' : period;

  /*
   * Settled days are ranked by crowns rather than by time. Once a day is over
   * the interesting question is which pun won, not who posted last. Today stays
   * newest-first, because the crown count is still moving.
   */
  const range: FeedRange =
    shown === 'today'
      ? { kind: 'day', day: today }
      : shown === 'yesterday'
        ? { kind: 'day', day: yesterday }
        : {
            kind: 'since',
            since: shiftPacificDay(today, -(WEEK_DAYS - 1)),
            // The week window is the one place the gate could leak today's puns
            // in through the side door, so it stops at yesterday until you post.
            through: locked ? yesterday : undefined,
          };

  const { puns, names, loading, error, dismissError, noFriends, crownSpent, react } = usePunFeed({
    scope,
    viewerId: userId,
    range,
    rankByCrowns: shown !== 'today',
  });

  if (checkingSubmission) {
    return <Spinner />;
  }

  // Before the gate, not after: "we could not check" is not the same answer as
  // "you have not posted", and showing the lock screen to someone who has
  // already posted is the bug this whole screen kept hitting.
  if (submissionCheckFailed) {
    return <CheckFailed onRetry={recheckSubmission} />;
  }

  /*
   * Your own pun is pinned to the top only for today, where the feed is a list
   * of what everyone wrote and finding yourself is convenient. On a settled day
   * the order *is* the result, so pinning would quietly put you above puns that
   * beat you.
   */
  const ordered =
    shown === 'today'
      ? [...puns.filter((pun) => pun.userId === userId), ...puns.filter((pun) => pun.userId !== userId)]
      : puns;

  return (
    <div>
      <div className="mb-3 flex gap-2">
        <Pill active={scope === 'everyone'} onClick={() => setScope('everyone')}>
          Everyone
        </Pill>
        <Pill active={scope === 'friends'} onClick={() => setScope('friends')}>
          Friends
        </Pill>
      </div>

      <div className="mb-6 flex flex-wrap gap-2">
        {(Object.keys(PERIOD_LABEL) as Period[]).map((option) => (
          <Pill
            key={option}
            active={shown === option}
            onClick={() => setPeriod(option)}
            disabled={locked && option === 'today'}
            small
          >
            {locked && option === 'today' ? `🔒 ${PERIOD_LABEL[option]}` : PERIOD_LABEL[option]}
          </Pill>
        ))}
      </div>

      {locked && <Locked />}

      {shown !== 'today' && <p className="mb-4 text-sm text-ink/50">Most crowned first.</p>}

      {error && (
        <button
          onClick={dismissError}
          className="mb-4 w-full rounded-xl border-2 border-ink bg-coral px-4 py-3 text-left text-sm font-medium text-white shadow-pop-sm"
        >
          {error}
          <span className="mt-1 block text-xs font-normal text-white/75">Tap to dismiss</span>
        </button>
      )}

      {loading ? (
        <Spinner />
      ) : noFriends ? (
        <Empty
          title="Party of pun."
          body="Follow a few people and their puns turn up here."
          action={{ to: '/friends', label: 'Find friends' }}
        />
      ) : ordered.length === 0 ? (
        <Empty
          title={scope === 'friends' ? 'Your friends are slacking.' : 'Nothing here yet.'}
          body={
            shown === 'today'
              ? 'You are suspiciously early. Check back later today.'
              : shown === 'yesterday'
                ? 'Nothing was posted yesterday.'
                : 'Nothing in the last week.'
          }
        />
      ) : (
        <div className="space-y-5">
          {ordered.map((pun) => (
            <PunCard
              key={pun.id}
              pun={pun}
              authorName={pun.userId === userId ? 'You' : (names[pun.userId] ?? '...')}
              viewerId={userId}
              crownSpent={crownSpent}
              onReact={(reaction) => react(pun, reaction)}
              highlight={pun.userId === userId}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function Pill({
  active,
  onClick,
  children,
  small,
  disabled,
}: {
  active: boolean;
  onClick: () => void;
  children: string;
  /** The second row is subordinate to the first, so it sits a size down. */
  small?: boolean;
  disabled?: boolean;
}) {
  return (
    <button
      onClick={onClick}
      aria-pressed={active}
      disabled={disabled}
      className={`rounded-full border-2 border-ink font-display font-bold shadow-pop-sm ${
        small ? 'px-4 py-1.5 text-xs' : 'px-5 py-2 text-sm'
      } ${
        disabled
          ? 'cursor-not-allowed bg-white/60 text-ink/40'
          : `press ${active ? 'bg-grape text-white' : 'bg-white hover:bg-grape/10'}`
      }`}
    >
      {children}
    </button>
  );
}

/**
 * Today is gated; the rest of the feed is not.
 *
 * A banner above the list rather than a screen in front of it. The full-screen
 * version answered "where are the puns" with a dead end, which is a strange
 * thing to show the one player who has not yet been given a reason to write.
 * Sitting under the locked Today pill, it explains the lock and leaves the
 * archive readable underneath.
 */
function Locked() {
  return (
    <section className="mb-6 flex flex-wrap items-center justify-between gap-4 rounded-2xl border-2 border-dashed border-ink/35 bg-white/60 px-5 py-4">
      <div>
        <p className="font-display text-lg font-bold">No pun, no fun.</p>
        <p className="mt-1 max-w-xs text-sm text-ink/60">
          Today&rsquo;s puns unlock the moment you post your own. Earlier days are open.
        </p>
      </div>
      <Link
        to="/today"
        className="press shrink-0 rounded-full border-2 border-ink bg-mustard px-6 py-2.5 font-display font-bold shadow-pop-sm"
      >
        Go write it
      </Link>
    </section>
  );
}

/**
 * Shown when the submission check errored rather than answered.
 *
 * Plain, like the missing-prompt panel on Today. Something is actually broken,
 * and a joke here reads as the app not taking it seriously.
 */
function CheckFailed({ onRetry }: { onRetry: () => void }) {
  return (
    <section className="rounded-2xl border-2 border-ink bg-white px-6 py-12 text-center shadow-pop">
      <p className="font-display text-xl font-bold">Could not check your pun.</p>
      <p className="mx-auto mt-2 max-w-xs text-sm text-ink/60">
        The connection dropped before we could tell whether you have posted today.
      </p>
      <button
        onClick={onRetry}
        className="press mt-7 rounded-full border-2 border-ink bg-lagoon px-7 py-3 font-display text-lg font-bold text-white shadow-pop"
      >
        Try again
      </button>
    </section>
  );
}

function Empty({
  title,
  body,
  action,
}: {
  title: string;
  body: string;
  action?: { to: string; label: string };
}) {
  return (
    <section className="rounded-2xl border-2 border-dashed border-ink/30 bg-white/60 px-6 py-12 text-center">
      <p className="font-display text-xl font-bold">{title}</p>
      <p className="mt-2 text-sm text-ink/60">{body}</p>
      {action && (
        <Link
          to={action.to}
          className="press mt-6 inline-block rounded-full border-2 border-ink bg-lagoon px-6 py-2.5 font-display font-bold text-white shadow-pop-sm"
        >
          {action.label}
        </Link>
      )}
    </section>
  );
}

function Spinner() {
  return (
    <div className="flex justify-center py-16">
      <span className="h-8 w-8 animate-spin rounded-full border-[3px] border-ink/15 border-t-ink" />
    </div>
  );
}

export default Feed;
